import { useEffect } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthShell } from "./components/AuthShell";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { ForgotPasswordPage } from "./pages/ForgotPasswordPage";
import { ResetPasswordPage } from "./pages/ResetPasswordPage";
import { VerifyEmailPage } from "./pages/VerifyEmailPage";
import { VerifyEmailResendPage } from "./pages/VerifyEmailResendPage";
import { SsoCallbackPage } from "./pages/SsoCallbackPage";
import { IdentitySettingsPage } from "./pages/IdentitySettingsPage";
import { OAuthApprovePage } from "./pages/OAuthApprovePage";
import { LegalAcceptancePage } from "./pages/LegalAcceptancePage";
import { TermsPage } from "./pages/TermsPage";
import { PrivacyPage } from "./pages/PrivacyPage";
import { LegalUpdatePage } from "./pages/LegalUpdatePage";
import { NotAcceptedPage } from "./pages/NotAcceptedPage";
import { HubLogoutPage } from "./pages/HubLogoutPage";

/** Alias redirect that carries ?redirect_uri=… (and any hash) through to the
 *  canonical /auth/* route — without it the redirect target is dropped and the
 *  user lands on the wrong site after sign-in. */
function AliasRedirect({ to }: { to: string }) {
  const location = useLocation();
  return (
    <Navigate
      to={{ pathname: to, search: location.search, hash: location.hash }}
      replace
    />
  );
}

export function App() {
  useEffect(() => {
    if (!document.getElementById("sc-identity-session-state")) {
      const el = document.createElement("div");
      el.id = "sc-identity-session-state";
      el.setAttribute("aria-live", "polite");
      el.style.display = "none";
      document.body.appendChild(el);
    }
  }, []);

  return (
    <Routes>
      <Route path="/logout" element={<HubLogoutPage />} />
      <Route path="/auth/logout" element={<HubLogoutPage />} />
      <Route element={<AuthShell />}>
        <Route path="/" element={<AliasRedirect to="/auth/login" />} />
        <Route path="/login" element={<AliasRedirect to="/auth/login" />} />
        <Route path="/signup" element={<AliasRedirect to="/auth/signup" />} />
        <Route path="/forgot-password" element={<AliasRedirect to="/auth/forgot-password" />} />
        <Route path="/reset-password" element={<AliasRedirect to="/auth/reset-password" />} />
        <Route path="/verify-email" element={<AliasRedirect to="/auth/verify-email" />} />
        <Route path="/verify/resend" element={<AliasRedirect to="/auth/verify/resend" />} />
        <Route path="/sso/callback" element={<AliasRedirect to="/auth/sso/callback" />} />
        <Route path="/oauth/approve" element={<AliasRedirect to="/auth/oauth/approve" />} />
        <Route path="/settings" element={<AliasRedirect to="/auth/settings" />} />
        <Route path="/acceptance" element={<AliasRedirect to="/auth/acceptance" />} />
        <Route path="/auth/login" element={<LoginPage />} />
        <Route path="/auth/signup" element={<SignupPage />} />
        <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
        <Route path="/auth/verify-email" element={<VerifyEmailPage />} />
        <Route path="/auth/verify/resend" element={<VerifyEmailResendPage />} />
        <Route path="/auth/sso/callback" element={<SsoCallbackPage />} />
        <Route path="/auth/oauth/approve" element={<OAuthApprovePage />} />
        <Route path="/auth/settings" element={<IdentitySettingsPage />} />
        <Route path="/auth/acceptance" element={<LegalAcceptancePage />} />
        <Route path="/legal/terms" element={<TermsPage />} />
        <Route path="/legal/privacy" element={<PrivacyPage />} />
        <Route path="/legal/legal-update" element={<LegalUpdatePage />} />
        <Route path="/legal/not-accepted" element={<NotAcceptedPage />} />
        <Route path="*" element={<AliasRedirect to="/auth/login" />} />
      </Route>
    </Routes>
  );
}
